// TODO - https://tracker.yandex.ru/ADTECH-454
import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Rating, Typography } from 'fronton-react';
import { cn } from 'utils/classnames';
import { patchUrlByLocale, seeMoreLinkOnClick } from 'utils';
import { getPathname } from 'common/selectors';
import { memo } from 'common/hocs';
import {
  pageContentStyles,
  topLineStyles,
  brandShelfTopLineStyles,
  carouselItemStyles,
  titleStyle,
  brandShelfTitleStyle,
  brandShelfWrapper,
  layoutStyles,
  seeMoreCarouselItem,
  carouselItemStylesSimplified,
} from './styles';
import type { ProductsCarouselComponentProps } from 'features/products/components/products-carousel';
import { ProductsCarouselControl } from 'features/products/components/products-carousel/products-carousel-control';
import { SponsoredProductsCarouselItem } from './products-carousel-item';
import { carouselWrapperStyles, helperRating } from 'features/products/components/products-carousel/styles';
import { getProductsData, getRatingsData, SponsoredProductType } from 'features/products';
import { Product } from 'common/components/product-card/types';
import { ProductsCarouselBottomLine } from 'features/products/components/products-carousel/products-carousel-bottom-line';
import { Carousel } from 'common/components';
import { useLoyaltyRewards } from 'common/hooks/use-loyalty-rewards';
import { useSponsoredProducts } from 'features/products/components/products-carousel/use-sponsored-products';
import { useTranslation } from 'react-i18next';
import { SeeMoreButton, SeeMoreButtonTop, topLineItemStyles } from 'common/components/products-carousel';
import { CarouselSubtitle } from 'common/components/sponsored-carousel/carousel-subtitle/carousel-subtitle';
import { CarouselBrandShelfSubtitle } from './brandshelf-subtitle';

export type SponsoredProductsCarouselProps = ProductsCarouselComponentProps & {
  isBrandShelf?: boolean;
  subtitle?: string;
  isSimplifiedVariant?: boolean;
};

export const SponsoredProductsCarousel = memo<SponsoredProductsCarouselProps>(
  ({
    isFromPdp = false,
    isBrandShelf = false,
    className,
    title,
    subtitle,
    showSeeMore = false,
    seeMoreLink = '/',
    slWidgetId,
    elementBlock = 'not set',
    'data-qa': dataQA,
    portalRootId = '',
    analytics,
    showLoyaltyRewards = false,
    sponsored,
    isSimplifiedVariant,
  }) => {
    const pathname = useSelector(getPathname);
    const products: Product[] | SponsoredProductType[] | null = useSelector(getProductsData);
    const ratings = useSelector(getRatingsData);

    const { products: productsToDisplay, shouldShowSponsored } = useSponsoredProducts(products, sponsored);

    const showSponsored = shouldShowSponsored(sponsored);
    const titleActual = showSponsored ? sponsored.title : title;

    useLoyaltyRewards({
      enabled: showLoyaltyRewards,
      products: productsToDisplay,
    });

    const [canScrollRight, setCanScrollRight] = useState(false);

    const { t } = useTranslation();
    const seeAllButtonCaption = t('ui.button.see-all');

    const showSeeMoreActual = showSponsored ? sponsored.showSeeMore : showSeeMore;
    const seeMoreLinkActual = patchUrlByLocale(
      showSponsored ? sponsored.seeMoreLink : seeMoreLink,
      pathname,
    );

    const seeMoreLinkOnClickHandler = useCallback(() => {
      seeMoreLinkOnClick(elementBlock, seeAllButtonCaption, seeMoreLinkActual, titleActual);
    }, [elementBlock, seeAllButtonCaption, seeMoreLinkActual, titleActual]);

    if (!productsToDisplay?.length) return null;

    let resolvedTitleStyle = '';
    if (isFromPdp) {
      resolvedTitleStyle = titleStyle;
    } else if (isBrandShelf) {
      resolvedTitleStyle = brandShelfTitleStyle;
    }

    const getWrapperClassName = () => {
      if (isSimplifiedVariant) return carouselItemStylesSimplified;
      if (isBrandShelf) return '';
      return carouselItemStyles;
    };

    return (
      <div className={cn(layoutStyles, className)}>
        <div className={pageContentStyles}>
          <Rating className={helperRating} value={0} />
          <div className={carouselWrapperStyles} data-qa={dataQA}>
            {!isBrandShelf && subtitle && <CarouselSubtitle subtitle={subtitle} />}

            {titleActual && (
              <div className={isBrandShelf ? brandShelfTopLineStyles : topLineStyles}>
                {titleActual && (
                  <div className={topLineItemStyles}>
                    {titleActual && (
                      <Typography
                        className={resolvedTitleStyle}
                        variant="s"
                        size="body_accent"
                        data-qa="products-carousel-title"
                      >
                        {titleActual}
                      </Typography>
                    )}
                  </div>
                )}
                {canScrollRight && showSeeMoreActual && (
                  <SeeMoreButtonTop
                    {...{ seeAllButtonCaption, seeMoreLinkActual, seeMoreLinkOnClickHandler }}
                  />
                )}
              </div>
            )}
            {isBrandShelf && subtitle && <CarouselBrandShelfSubtitle subtitle={subtitle} />}

            <Carousel
              className={isBrandShelf ? brandShelfWrapper : ''}
              canScrollCallBack={(_, right) => setCanScrollRight(right ?? false)}
              controlLeft={<ProductsCarouselControl direction="left" />}
              controlRight={<ProductsCarouselControl direction="right" />}
              data-qa="products-carousel"
              slWidgetId={slWidgetId}
              variant="sponsored"
            >
              {productsToDisplay.map((product, index) => (
                <SponsoredProductsCarouselItem
                  isBrandShelf={isBrandShelf}
                  key={product.productId + index}
                  className={getWrapperClassName()}
                  index={index + 1}
                  product={product}
                  ratings={ratings}
                  elementBlock={elementBlock}
                  data-sl-product-slot={index}
                  analytics={analytics}
                  isSimplifiedVariant={isSimplifiedVariant}
                  isSponsored={!!sponsored}
                />
              ))}
              {isBrandShelf && showSeeMoreActual && !!productsToDisplay.length && (
                <div className={cn(seeMoreCarouselItem)} data-qa="products-carousel-item-seemore">
                  <SeeMoreButton
                    {...{
                      seeAllButtonCaption,
                      seeMoreLinkActual,
                      seeMoreLinkOnClickHandler,
                      isSponsored: true,
                    }}
                  />
                </div>
              )}
            </Carousel>

            {isFromPdp && (
              <ProductsCarouselBottomLine
                products={productsToDisplay}
                slWidgetId={slWidgetId}
                ratings={ratings}
                elementBlock={elementBlock}
                title={titleActual}
                portalRootId={portalRootId}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);
